import React, { useState } from 'react';
import { Card, Table, Tag, Button, Modal, Form, Input, Select, Space, message, Tabs, Row, Col, Statistic, Tooltip } from 'antd';
import {
  AlertOutlined, PlusOutlined, DeleteOutlined, EditOutlined, BellOutlined,
  CheckCircleOutlined, WarningOutlined, ThunderboltOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useListAlertasQuery, useAddAlertaMutation, useEditAlertaMutation, useDeleteAlertaMutation,
  useListAlertasLogQuery, useListServersQuery, useListProjectsQuery, useEvaluarAlertasMutation
} from '../store/api';

const { Option } = Select;

const tipoLabels = { cpu: 'CPU', ram: 'RAM', disco: 'Disco', url_down: 'URL Down', ssl_expiry: 'SSL Expiración', dominio_expira: 'Dominio Expira', custom: 'Custom' };
const tipoColors = { cpu: 'orange', ram: 'blue', disco: 'purple', url_down: 'red', ssl_expiry: 'gold', dominio_expira: 'magenta', custom: 'default' };
const condLabels = { mayor: '>', menor: '<', igual: '=', diferente: '≠' };

const Alertas = () => {
  const { data: alertas = [], isLoading } = useListAlertasQuery();
  const { data: logs = [], isLoading: logsLoading } = useListAlertasLogQuery();
  const { data: servers = [] } = useListServersQuery();
  const { data: projects = [] } = useListProjectsQuery();
  const [addAlerta] = useAddAlertaMutation();
  const [editAlerta] = useEditAlertaMutation();
  const [deleteAlerta] = useDeleteAlertaMutation();
  const [evaluarAlertas, { isLoading: evaluating }] = useEvaluarAlertasMutation();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const activas = alertas.filter(a => a.activo == 1).length;
  const inactivas = alertas.length - activas;

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await editAlerta({ id: editing.id, ...values }).unwrap();
        message.success('Alerta actualizada');
      } else {
        await addAlerta(values).unwrap();
        message.success('Alerta creada');
      }
      setModal(false); setEditing(null); form.resetFields();
    } catch { message.error('Error al guardar alerta'); }
  };

  const openEdit = (r) => {
    setEditing(r);
    form.setFieldsValue({
      nombre: r.nombre,
      tipo: r.tipo,
      servidor_id: r.servidor_id || undefined,
      proyecto_id: r.proyecto_id || undefined,
      umbral: r.umbral,
      condicion: r.condicion,
      canal: r.canal,
      email_destino: r.email_destino,
      whatsapp_destino: r.whatsapp_destino,
      activo: r.activo,
      cooldown_min: r.cooldown_min,
    });
    setModal(true);
  };

  const alertColumns = [
    { title: 'Nombre', dataIndex: 'nombre', render: n => <strong>{n}</strong>, ellipsis: true },
    { title: 'Tipo', dataIndex: 'tipo', width: 110, render: t => <Tag color={tipoColors[t]}>{tipoLabels[t] || t}</Tag>,
      filters: Object.entries(tipoLabels).map(([k, v]) => ({ text: v, value: k })), onFilter: (v, r) => r.tipo === v },
    { title: 'Servidor', dataIndex: 'servidor_nombre', render: t => t || <span style={{ color: '#bbb' }}>Todos</span> },
    { title: 'Condición', key: 'cond', width: 120, render: (_, r) => {
      const unit = ['cpu', 'ram', 'disco'].includes(r.tipo) ? '%' : ['ssl_expiry', 'dominio_expira'].includes(r.tipo) ? ' días' : '';
      return <code>{condLabels[r.condicion] || r.condicion} {r.umbral}{unit}</code>;
    }},
    { title: 'Canal', dataIndex: 'canal', width: 100, render: c => {
      if (!c) return '-';
      return c.split(',').map(ch => <Tag key={ch} color={ch === 'email' ? 'blue' : ch === 'whatsapp' ? 'green' : 'purple'}>{ch}</Tag>);
    }},
    { title: 'Cooldown', dataIndex: 'cooldown_min', width: 90, render: m => m ? `${m} min` : '-' },
    { title: 'Estado', dataIndex: 'activo', width: 90, render: a => <Tag color={a == 1 ? 'green' : 'default'}>{a == 1 ? 'Activa' : 'Inactiva'}</Tag> },
    { title: 'Última', dataIndex: 'ultima_alerta', width: 120, render: d => d ? dayjs(d).format('DD/MM HH:mm') : <span style={{ color: '#bbb' }}>Nunca</span> },
    { title: '', key: 'act', width: 80, render: (_, r) => (
      <Space size={4}>
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => Modal.confirm({
          title: '¿Eliminar alerta?', content: r.nombre,
          onOk: async () => { await deleteAlerta(r.id).unwrap(); message.success('Eliminada'); }
        })} />
      </Space>
    )},
  ];

  const logColumns = [
    { title: 'Fecha', dataIndex: 'created_at', render: d => dayjs(d).format('DD/MM/YYYY HH:mm'), width: 140,
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(), defaultSortOrder: 'descend' },
    { title: 'Alerta', dataIndex: 'alerta_nombre', render: n => n || '-' },
    { title: 'Valor', dataIndex: 'valor_actual', width: 80 },
    { title: 'Canal', dataIndex: 'canal', width: 80, render: c => <Tag color={c === 'email' ? 'blue' : 'purple'}>{c}</Tag> },
    { title: 'Enviado', dataIndex: 'enviado', width: 80, render: e => <Tag color={e ? 'green' : 'red'}>{e ? 'Sí' : 'No'}</Tag> },
    { title: 'Mensaje', dataIndex: 'mensaje', ellipsis: true },
    { title: 'Error', dataIndex: 'error_envio', ellipsis: true, render: e => e ? <Tooltip title={e}><Tag color="red">Error</Tag></Tooltip> : null },
  ];

  return (
    <div className="page-container fade-in">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title"><AlertOutlined style={{ marginRight: 8 }} />Alertas</h1>
          <div className="page-subtitle">{alertas.length} alertas configuradas</div>
        </div>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); form.resetFields(); setModal(true); }}>Nueva Alerta</Button>
          <Button icon={<ThunderboltOutlined />} loading={evaluating} onClick={async () => {
            try { const r = await evaluarAlertas().unwrap(); message.success(r.message || `${r.triggered} alertas disparadas`); }
            catch { message.error('Error al evaluar'); }
          }}>Evaluar Ahora</Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={8}><Card size="small"><Statistic title="Activas" value={activas} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="Inactivas" value={inactivas} prefix={<WarningOutlined />} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="Disparadas" value={logs.length} valueStyle={{ color: '#fa8c16' }} prefix={<ThunderboltOutlined />} /></Card></Col>
      </Row>

      <Card bordered={false}>
        <Tabs defaultActiveKey="config" items={[
          { key: 'config', label: <span><AlertOutlined /> Configuración ({alertas.length})</span>,
            children: <Table columns={alertColumns} dataSource={alertas} rowKey="id" loading={isLoading}
              pagination={{ pageSize: 15, showTotal: t => `${t} alertas` }} size="middle" scroll={{ x: true }} /> },
          { key: 'log', label: <span><BellOutlined /> Historial ({logs.length})</span>,
            children: <Table columns={logColumns} dataSource={logs} rowKey="id" loading={logsLoading}
              pagination={{ pageSize: 15, showTotal: t => `${t} registros` }} size="middle" scroll={{ x: true }} /> },
        ]} />
      </Card>

      <Modal title={editing ? 'Editar Alerta' : 'Nueva Alerta'} open={modal} onOk={handleSave}
        onCancel={() => { setModal(false); setEditing(null); form.resetFields(); }} okText="Guardar" width={600}>
        <Form form={form} layout="vertical">
          <Form.Item name="nombre" label="Nombre" rules={[{ required: true, message: 'Ingrese un nombre' }]}>
            <Input placeholder="Ej: CPU alta en Producción" />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="tipo" label="Tipo de métrica" rules={[{ required: true }]}>
              <Select placeholder="Seleccionar">
                {Object.entries(tipoLabels).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="servidor_id" label="Servidor">
              <Select allowClear placeholder="Todos los servidores" showSearch optionFilterProp="children">
                {servers.map(s => <Option key={s.id} value={s.id}>{s.nombre}</Option>)}
              </Select>
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <Form.Item name="condicion" label="Condición" initialValue="mayor">
              <Select>{Object.entries(condLabels).map(([k, v]) => <Option key={k} value={k}>{v} ({k})</Option>)}</Select>
            </Form.Item>
            <Form.Item name="umbral" label="Umbral" rules={[{ required: true }]}><Input type="number" step="0.01" /></Form.Item>
            <Form.Item name="cooldown_min" label="Cooldown (min)" initialValue={30}><Input type="number" /></Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="canal" label="Canal" initialValue="email">
              <Select><Option value="email">Email</Option><Option value="whatsapp">WhatsApp</Option><Option value="email,whatsapp">Ambos</Option></Select>
            </Form.Item>
            <Form.Item name="activo" label="Estado" initialValue={1}>
              <Select><Option value={1}>Activa</Option><Option value={0}>Inactiva</Option></Select>
            </Form.Item>
          </div>
          <Form.Item name="email_destino" label="Email destino"><Input placeholder="admin@example.com" /></Form.Item>
          <Form.Item name="whatsapp_destino" label="WhatsApp destino (opcional)"><Input placeholder="573001234567" /></Form.Item>
          <Form.Item name="proyecto_id" label="Proyecto (opcional)">
            <Select allowClear placeholder="Sin proyecto">{projects.map(p => <Option key={p.id} value={p.id}>{p.nombre}</Option>)}</Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Alertas;
